import { CSSProperties, FormEvent, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Input } from '../atoms';
import { useToast } from '../organisms';
import brandMark from '../assets/brand/ignite-icon-light.svg';
import { LegalFooterLinks } from './LegalPage';

/**
 * LoginPage — v2 auth surface at /login (FR-6 cutover: V2 owns '/').
 *
 * Composed pattern (no canonical M3 login page; M3 documents Text Field +
 * Filled Button on a centered Card surface). Per Agent Protocol step 2,
 * derives from M3 form patterns (text-fields + buttons + cards spec).
 *
 * Two sign-in paths, driven by the real, public /api/auth/methods flags:
 *   - Staff / demo sign-in — the shared-secret password field. POSTs
 *     /api/auth/login with `{password}` (legacy shared secret) OR is also the
 *     surface a staff user uses.
 *   - Client portal sign-in — depends on deployment:
 *       • DEMO (methods.demoMode): a direct email + password form that POSTs
 *         /api/auth/login `{email,password}` (per-user bcrypt). WorkOS redirect
 *         URIs aren't wired for the demo host, so the SSO bounce is replaced by
 *         this real client login. On success we hard-reload to '/' so the app
 *         re-boots and routes the client (role=client) into the portal.
 *       • PROD (methods.workos && !demoMode): the AuthKit SSO bounce to
 *         /api/auth/workos/start (unchanged).
 *
 * Backend wire:
 *   - POST /api/auth/login → sets `ignite_session` cookie. 401 on failure.
 *   - GET /api/auth/methods → { workos, demoMode } booleans (no secrets).
 *   - The server serves the SPA shell for any unauthenticated navigation
 *     (src/app.js session gate), so this page renders without a session.
 *     Old /v2/login links 301-redirect to /login for back-compat.
 */

// Demo client credentials surfaced as an on-page hint + quick-pick. These are
// non-secret demo fixtures (shown only when the server reports demoMode) so a
// demo operator can sign in as a specific client without out-of-band notes.
const DEMO_CLIENTS = [
  { label: 'Bluebonnet Holdings', email: 'robert@bluebonnet.example' },
  { label: 'Peachtree Partners', email: 'linda@peachtree.example' },
];
const DEMO_CLIENT_PASSWORD = 'Demo!Pass2026';

export function LoginPage() {
  // ── Staff / demo shared-secret path ──
  const [staffEmail, setStaffEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Client portal path (demo: email+password) ──
  const [clientEmail, setClientEmail] = useState('');
  const [clientPassword, setClientPassword] = useState('');
  const [clientSubmitting, setClientSubmitting] = useState(false);
  const [clientError, setClientError] = useState<string | null>(null);

  // Sign-in surfaces to offer, from the real public /api/auth/methods flags.
  const [workosEnabled, setWorkosEnabled] = useState(false);
  const [demoMode, setDemoMode] = useState(false);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const next = params.get('next') || '/';

  useEffect(() => {
    let active = true;
    // Promise.resolve() so a non-promise fetch mock (unit tests) can't throw here.
    Promise.resolve(fetch('/api/auth/methods', { credentials: 'same-origin' }))
      .then((r) => (r && r.ok ? r.json() : { workos: false, demoMode: false }))
      .then((m) => {
        if (!active) return;
        setWorkosEnabled(!!(m && m.workos));
        setDemoMode(!!(m && m.demoMode));
      })
      .catch(() => { /* leave the alternate entries hidden on error */ });
    return () => { active = false; };
  }, []);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        // Send the email when one is given so the server takes its per-user
        // bcrypt path; omitting it falls through to the legacy shared-secret
        // path, which only answers when APP_PASSWORD is configured.
        body: JSON.stringify(staffEmail ? { email: staffEmail, password } : { password }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setError(body.error || 'Invalid credentials');
        toast({ variant: 'error', message: body.error || 'Invalid credentials' });
        return;
      }
      // Invalidate the TanStack Query cache for current-user so the
      // dashboard does not hit the stale 401 error cached with retry:false
      // and immediately redirect back to /login (the auth loop bug).
      await queryClient.invalidateQueries({ queryKey: ['current-user'] });
      toast({ variant: 'success', message: 'Signed in.' });
      navigate(next, { replace: true });
    } catch (err) {
      const msg = (err as Error).message || 'Network error';
      setError(msg);
      toast({ variant: 'error', message: msg });
    } finally {
      setSubmitting(false);
    }
  }

  // Shared client login — used by the manual form AND the demo quick-pick.
  // Takes creds as args (not state) so the quick-pick can sign in in one click
  // without waiting for the async setState to flush.
  async function loginAsClient(email: string, password: string) {
    setClientError(null);
    setClientSubmitting(true);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ email, password }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setClientError(body.error || 'Invalid credentials');
        toast({ variant: 'error', message: body.error || 'Invalid credentials' });
        return;
      }
      toast({ variant: 'success', message: 'Signed in.' });
      // Hard reload to '/' so the SPA re-boots with the fresh session and the
      // app's role-based router lands the client (role=client) in the portal.
      window.location.assign('/');
    } catch (err) {
      const msg = (err as Error).message || 'Network error';
      setClientError(msg);
      toast({ variant: 'error', message: msg });
    } finally {
      setClientSubmitting(false);
    }
  }

  function handleClientSubmit(e: FormEvent) {
    e.preventDefault();
    loginAsClient(clientEmail, clientPassword);
  }

  const labelStyle: CSSProperties = {
    display: 'block',
    marginBottom: 'var(--sp-1)',
    fontSize: 'var(--fs-sm)',
    fontWeight: 'var(--fw-medium)',
    color: 'var(--color-ink)',
  };
  const sectionTitleStyle: CSSProperties = {
    margin: '0 0 var(--sp-3)',
    fontSize: 'var(--fs-sm)',
    fontWeight: 'var(--fw-semibold)',
    color: 'var(--color-ink)',
    letterSpacing: 'var(--ls-tight)',
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'grid',
      placeItems: 'center',
      background: 'var(--color-bg)',
      padding: 'var(--sp-5)',
    }}>
      <div style={{
        width: '100%',
        maxWidth: 400,
        background: 'var(--color-card)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--r-2xl)',
        boxShadow: 'var(--sh-lg)',
        padding: 'var(--sp-7)',
      }}>
        <div style={{ textAlign: 'center', marginBottom: 'var(--sp-6)' }}>
          {/* Lockup composed in HTML rather than shipped as one SVG. A single
              logo file has to guess where its own ink ends, so any slack in the
              viewBox centres the BOX and leaves the artwork visibly off-centre —
              and the wordmark's width depends on whether the brand font loaded.
              Flexbox measures the real rendered mark and text instead. */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 10, marginBottom: 'var(--sp-2)',
          }}>
            <img src={brandMark} alt="" aria-hidden="true" style={{ width: 34, height: 34, display: 'block' }} />
            <span style={{
              fontSize: 30, fontWeight: 700, letterSpacing: '-0.02em',
              color: 'var(--color-ink)', lineHeight: 1,
            }}>Ignite</span>
          </div>
          <h1 style={{
            position: 'absolute', width: 1, height: 1, padding: 0, margin: -1,
            overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap', border: 0,
          }}>Ignite</h1>
          <p style={{
            margin: 'var(--sp-1) 0 0',
            color: 'var(--color-ink-3)',
            fontSize: 'var(--fs-sm)',
          }}>Sign in to continue</p>
        </div>

        {/* ── Staff / demo sign-in ── */}
        <section>
          <h2 style={sectionTitleStyle}>Staff sign-in</h2>
          <form onSubmit={handleSubmit}>
            <label style={{ display: 'block', marginBottom: 'var(--sp-3)' }}>
              <span style={labelStyle}>Email</span>
              <Input
                type="email"
                value={staffEmail}
                onChange={e => setStaffEmail(e.target.value)}
                autoComplete="username"
                invalid={!!error}
                placeholder="you@organization.org"
              />
            </label>
            <label style={{ display: 'block', marginBottom: 'var(--sp-4)' }}>
              <span style={labelStyle}>Password</span>
              <Input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoComplete="current-password"
                invalid={!!error}
              />
              {error && (
                <div style={{
                  marginTop: 'var(--sp-1)',
                  fontSize: 'var(--fs-sm)',
                  color: 'var(--color-status-red)',
                }}>{error}</div>
              )}
            </label>
            <Button
              type="submit"
              variant="primary"
              size="md"
              loading={submitting}
              disabled={!password || submitting}
              style={{ width: '100%' }}
            >
              Sign in
            </Button>
          </form>
        </section>

        {/* ── Client portal sign-in ── */}
        {(demoMode || workosEnabled) && (
          <section style={{ marginTop: 'var(--sp-6)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-3)', marginBottom: 'var(--sp-5)' }}>
              <div style={{ flex: 1, height: 1, background: 'var(--color-border)' }} />
              <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>or</span>
              <div style={{ flex: 1, height: 1, background: 'var(--color-border)' }} />
            </div>
            <h2 style={sectionTitleStyle}>Client portal sign-in</h2>

            {demoMode ? (
              <form onSubmit={handleClientSubmit}>
                <label style={{ display: 'block', marginBottom: 'var(--sp-4)' }}>
                  <span style={labelStyle}>Email</span>
                  <Input
                    type="email"
                    value={clientEmail}
                    onChange={e => setClientEmail(e.target.value)}
                    autoComplete="username"
                    invalid={!!clientError}
                    placeholder="you@example.com"
                  />
                </label>
                <label style={{ display: 'block', marginBottom: 'var(--sp-4)' }}>
                  <span style={labelStyle}>Password</span>
                  <Input
                    type="password"
                    value={clientPassword}
                    onChange={e => setClientPassword(e.target.value)}
                    autoComplete="current-password"
                    invalid={!!clientError}
                    placeholder="Password"
                  />
                  {clientError && (
                    <div style={{
                      marginTop: 'var(--sp-1)',
                      fontSize: 'var(--fs-sm)',
                      color: 'var(--color-status-red)',
                    }}>{clientError}</div>
                  )}
                </label>
                <Button
                  type="submit"
                  variant="secondary"
                  size="md"
                  loading={clientSubmitting}
                  disabled={!clientEmail || !clientPassword || clientSubmitting}
                  style={{ width: '100%' }}
                >
                  Sign in to portal
                </Button>

                {/* Demo credential hint + quick-pick — only ever shown in demoMode. */}
                <div style={{
                  marginTop: 'var(--sp-4)',
                  padding: 'var(--sp-3)',
                  borderRadius: 'var(--r-lg)',
                  background: 'var(--color-bg)',
                  border: '1px dashed var(--color-border)',
                }}>
                  <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)', marginBottom: 'var(--sp-2)' }}>
                    Demo clients — click to sign in
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-2)' }}>
                    {DEMO_CLIENTS.map((c) => (
                      <button
                        key={c.email}
                        type="button"
                        disabled={clientSubmitting}
                        onClick={() => {
                          setClientEmail(c.email);
                          setClientPassword(DEMO_CLIENT_PASSWORD);
                          setClientError(null);
                          loginAsClient(c.email, DEMO_CLIENT_PASSWORD);
                        }}
                        style={{
                          appearance: 'none',
                          cursor: 'pointer',
                          border: '1px solid var(--color-border)',
                          background: 'var(--color-card)',
                          color: 'var(--color-ink)',
                          borderRadius: 'var(--r-md)',
                          padding: 'var(--sp-1) var(--sp-3)',
                          fontSize: 'var(--fs-xs)',
                          fontFamily: 'inherit',
                        }}
                      >
                        {c.label}
                        <span style={{ color: 'var(--color-ink-3)' }}> · {c.email}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </form>
            ) : (
              <>
                <a
                  href="/api/auth/workos/start"
                  style={{
                    display: 'block', width: '100%', boxSizing: 'border-box', textAlign: 'center',
                    padding: 'var(--sp-3)', borderRadius: 'var(--r-lg)',
                    border: '1px solid var(--color-border)', background: 'var(--color-card)',
                    color: 'var(--color-ink)', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)',
                    textDecoration: 'none',
                  }}
                >
                  Client portal sign-in
                </a>
                <p style={{ margin: 'var(--sp-2) 0 0', textAlign: 'center', fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' }}>
                  Clients sign in here to view their properties, documents, and messages.
                </p>
              </>
            )}
          </section>
        )}

        <footer style={{
          marginTop: 'var(--sp-6)',
          paddingTop: 'var(--sp-4)',
          borderTop: '1px solid var(--color-border)',
          textAlign: 'center',
        }}>
          <a href="/v1/login.html" style={{ color: 'var(--color-ink-3)', fontSize: 'var(--fs-xs)', textDecoration: 'none' }}>
            Use v1 login
          </a>
          {/* Legal notices, reachable before anyone signs in — which is the only
              point at which a privacy notice is actually useful. */}
          <LegalFooterLinks style={{ marginTop: 'var(--sp-3)' }} />
        </footer>
      </div>
    </div>
  );
}
